import { useState, useEffect, createContext } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Styled from "./AppStyles";

import {
  Search,
  Edit,
  Manage,
  CopyToClipboard,
  ColorSAP,
  Info,
} from "./ontego-ui";
import { useNavigate } from "react-router-dom";

import { ROLE_ADMIN } from "./Constants/ClientRoles";
import { FUNCTION_SONDERFUNKTIONEN_ERWEITERUNGEN } from "./Constants/FunctionCollection";
import {
  ADMIN_DASHBOARD,
  BLACKLISTED_DEVICE,
  CUSTOMIZE_MAIN_DASHBOARD_PATH,
  FUNCTION_DETAILS_PATH,
  LOGIN_PATH,
  MANAGE_MAIN_DASHBOARD_PATH,
  REGISTER_PATH,
  SCENARIO_PATH,
  SPECIAL_FUNCTIONS_PATH,
} from "./Constants/Path";

import AppService from "./AppService";
import AuthService from "./Pages/Login/AuthService";

import Login from "./Pages/Login/Login";
import Register from "./Pages/Register/Register";

import AdminDashboard from "./Pages/AdminDashboard/AdminDashboard";
import BlacklistedDevice from "./Pages/BlacklistedDevice/BlacklistedDevice";

import FunctionDetailsContainer from "./Pages/FunctionDetails/FunctionDetailsContainer";
import Scenario from "./Pages/Scenario/Scenario";

import ManageMainDashboard from "./Pages/Manage/ManageMainDashboard";

import CollectionCartDialog from "./Dialogs/CollectionCart/CollectionCart";
import PricingAndLicenseDialog from "./Dialogs/PricingAndLicense/PricingAndLicense";
import UpAndRunningDialog from "./Dialogs/UpAndRunning/UpAndRunning";

import Cart from "./Models/Cart";
import GeneralBackdrop from "./ReusableComponents/Backdrop/GeneralBackdrop";
import LoginBackdrop from "./ReusableComponents/Backdrop/LoginBackdrop";
import ScreenSizeWarningBackdrop from "./ReusableComponents/Backdrop/ScreenSizeWarningBackdrop";

import PrivateRoute from "./Utils/Route/PrivateRoute";
import PublicRoute from "./Utils/Route/PublicRoute";

import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import Navbar from "./ReusableComponents/Navbar/Navbar";
import SAPTopNav from "./ReusableComponents/SAPTopNav/SAPTopNav";
import Snackbar from "./ReusableComponents/Snackbar/Snackbar";

import { Check } from "./ontego-ui/sap_icons";
import {
  calculateCollectionSize,
  convertNotesToEditorState,
  convertNotesToJSON,
  convertUpAndRunningNotesToEditorState,
  usePersistedState,
} from "./Utils/Utils";
import { redirectToLogin } from "./Utils/Auth";

export const AppContext = createContext({});
const AppContextProvider = AppContext.Provider;

export const PaddedContainer = (props) => {
  const { children, style } = props;

  return (
    <Styled.PaddedContainer style={style}>{children}</Styled.PaddedContainer>
  );
};

export const RootWrapperContainer = (props) => {
  const { children, rootStyle, childWrapperStyle } = props;
  return (
    <Styled.RootContainer style={{ ...rootStyle }}>
      <Styled.RootContainerChildWrapper style={{ ...childWrapperStyle }}>
        {children}
      </Styled.RootContainerChildWrapper>
    </Styled.RootContainer>
  );
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 1024,
    },
  },
});

const RootContainer = (props) => {
  return (
    <ThemeProvider theme={theme}>
      <div style={{ width: "100%", height: "100%" }}>{props.children}</div>
    </ThemeProvider>
  );
};

let snackbarTimeout = null;
let errorSnackbarTimeout = null;

const LoginPage = () => {
  return <Login />;
};

const discover = "Discover";
const manage = "Manage";

const getDirectories = () => {
  return [
    {
      icon: <Search />,
      label: discover,
      isActive:
        window.location.pathname !== CUSTOMIZE_MAIN_DASHBOARD_PATH &&
        window.location.pathname !== MANAGE_MAIN_DASHBOARD_PATH,
      path: SCENARIO_PATH,
      disabled: false,
    },
    {
      icon: <Edit />,
      label: "Customize",
      isActive: window.location.pathname === CUSTOMIZE_MAIN_DASHBOARD_PATH,
      path: CUSTOMIZE_MAIN_DASHBOARD_PATH,
      disabled: false,
    },
    {
      icon: <Manage style={{ width: 36, height: 36 }} />,
      label: manage,
      isActive: window.location.pathname === MANAGE_MAIN_DASHBOARD_PATH,
      path: MANAGE_MAIN_DASHBOARD_PATH,
      disabled: false,
    },
  ];
};

const App = () => {
  const theme = useTheme();
  const isScreenSizeLimit = useMediaQuery(theme.breakpoints.down("1280"));

  const navigate = useNavigate();

  const location = useLocation();
  const isAuthPage =
    location.pathname === LOGIN_PATH || location.pathname === REGISTER_PATH;
  const isScenarioPage = location.pathname === SCENARIO_PATH;

  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isFunctionLoading, setIsFunctionLoading] = useState(false);

  const [userInfo, setUserInfo] = useState(null);
  const [client, setClient] = useState({});

  const [inspectedSystem, setInspectedSystem] = useState(null);
  const [inspectedVersion, setInspectedVersion] = useState(null);

  const [specialFunctions, setSpecialFunctions] = useState(
    FUNCTION_SONDERFUNKTIONEN_ERWEITERUNGEN,
  );

  const [users, setUsers] = useState([]);
  const [signingKeys, setSigningKeys] = useState([]);
  const [devices, setDevices] = useState([]);
  const [devicesWithoutClient, setDevicesWithoutClient] = useState([]);
  const [deviceGroups, setDeviceGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roles2properties, setRoles2properties] = useState([]);

  const [directories, setDirectories] = useState(getDirectories(false));

  useEffect(() => {
    const info = AuthService.getUserInfo();
    if (info && !AuthService.checkIfTokenExpired()) {
      const userInformation = jwtDecode(info);

      AppService.getClientInfo(userInformation.key)
        .then((response) => {
          setClient(response.data);
          setDirectories(getDirectories(response.data.hasPurchased));
          window.gtag("config", "G-9CF4SB048S", {
            user_id: response.data.name,
          });
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            logoutAction();
          }
        });

      AppService.getFunctionCollection(userInformation.key)
        .then((res) => {
          if (
            Object.keys(res.data).length === 0 &&
            res.data.constructor === Object
          ) {
            setCollection([]);
          } else {
            const newCollection = [...res.data.functionCollection];

            setCollection(convertNotesToEditorState(newCollection));
          }
        })
        .catch((error) => {
          setCollection([]);
          console.log(error);
        });

      AppService.getUpAndRunningCollection(userInformation.key)
        .then((res) => {
          if (
            Object.keys(res.data).length === 0 &&
            res.data.constructor === Object
          ) {
            setUpAndRunningCollection({});
          } else {
            setUpAndRunningCollection(
              convertUpAndRunningNotesToEditorState(
                res.data.upAndRunningCollection,
              ),
            );
          }
        })
        .catch((error) => {
          setUpAndRunningCollection({});
          console.log(error);
        });
    }
  }, [userInfo]);

  const [inspectedFunctionId, setInspectedFunctionId] = usePersistedState(
    "inspectedFunctionId",
    ``,
  );
  const handleChangeInspectedFunctionId = (val) => setInspectedFunctionId(val);

  const [collection, setCollection] = usePersistedState("collection", []);
  const [upAndRunningCollection, setUpAndRunningCollection] = useState({});

  const [editorStateList, setEditorStateList] = useState([]);

  const handleAddEditorStateList = (editorStateData) => {
    setEditorStateList(editorStateData);
  };

  const handleChangeCollection = (func) => (e) => {
    e.stopPropagation();

    const newCollection = [...collection];

    let alreadyExists = false;

    newCollection.forEach((c) => {
      if (c.name === func.name) {
        alreadyExists = true;
      }
    });

    if (!alreadyExists) {
      newCollection.push(
        new Cart(func.systemID, func.name, func.icon, func.typeOf),
      );
    } else {
      newCollection.forEach((c, i) => {
        if (c.name === func.name) {
          newCollection.splice(i, 1);
        }
      });
    }

    AppService.updateFunctionCollection(
      userInfo.key,
      convertNotesToJSON(newCollection),
    )
      .then((res) => {
        setCollection(convertNotesToEditorState(newCollection));
        if (!alreadyExists) {
          handleShowSnackbar({
            message: t("common.addedToCollection"),
            autoHideDuration: 5000,
            icon: <CopyToClipboard />,
            showButton: true,
            buttonLabel: "Look Collection",
            onUndo: () => {
              handleOpenCollectionCartDialog();
            },
          });
        } else {
          handleShowSnackbar({
            message: t("common.removedFromCollection"),
            autoHideDuration: 5000,
            icon: <CopyToClipboard />,
            showButton: true,
            buttonLabel: "Look Collection",
            onUndo: () => {
              handleOpenCollectionCartDialog();
            },
          });
        }
      })
      .catch((error) => handleShowErrorSnackbar(error));
  };

  const [upAndRunningDialogOpen, setUpAndRunningDialogOpen] = useState(false);
  const handleOpenUpAndRunningDialog = () => setUpAndRunningDialogOpen(true);
  const handleCloseUpAndRunningDialog = () => setUpAndRunningDialogOpen(false);

  const [pricingAndLicenseDialogOpen, setPricingAndLicenseDialogOpen] =
    useState(false);
  const handleOpenPricingAndLicenseDialog = () =>
    setPricingAndLicenseDialogOpen(true);
  const handleClosePricingAndLicenseDialog = () =>
    setPricingAndLicenseDialogOpen(false);

  const [collectionCartDialogOpen, setCollectionCartDialogOpen] =
    useState(false);
  const handleOpenCollectionCartDialog = () =>
    setCollectionCartDialogOpen(true);
  const handleCloseCollectionCartDialog = () =>
    setCollectionCartDialogOpen(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [snackbarErrorMessage, setSnackbarErrorMessage] = useState(false);

  const { t, i18n } = useTranslation();

  const [snackbarData, setSnackbarData] = useState({
    key: "snackbar",
    message: "",
    icon: <Check />,
    showButton: false,
    buttonLabel: t("manage.close"),
    onClick: () => {},
  });
  const [errorSnackbarData, setErrorSnackbarData] = useState({
    key: "errorSnackbar",
  });

  const [showDirectory, setShowDirectory] = useState(false);

  const handleShowSnackbar = ({
    message,
    autoHideDuration = 1000,
    icon = <Check />,
    showButton = false,
    buttonLabel = t("manage.close"),
    onUndo = () => {},
  }) => {
    setShowSnackbar(true);
    setSnackbarData({
      key: new Date().getTime(),
      message: message,
      autoHideDuration: autoHideDuration,
      icon: icon,
      showButton: showButton,
      buttonLabel: buttonLabel,
      onClick: () => {
        handleCloseSnackbar();
        onUndo();
      },
    });
    clearTimeout(snackbarTimeout);
    snackbarTimeout = setTimeout(() => {
      handleCloseSnackbar();
    }, autoHideDuration);
  };

  const handleShowErrorSnackbar = (error) => {
    if (error && error.response && error.response.status === 401) {
      logoutAction();
      setSnackbarErrorMessage("Your session has expired.");
    }
    setShowErrorSnackbar(true);
    setErrorSnackbarData({
      key: new Date().getTime(),
    });
    clearTimeout(errorSnackbarTimeout);
    errorSnackbarTimeout = setTimeout(() => {
      handleCloseErrorSnackbar();
    }, 3000);
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
    clearTimeout(snackbarTimeout);
  };

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
    setTimeout(() => {
      setSnackbarErrorMessage(null);
    }, 100);
    clearTimeout(errorSnackbarTimeout);
  };

  const topNavbarLogoClick = () => {
    if (userInfo && userInfo.role === ROLE_ADMIN) {
      navigate(ADMIN_DASHBOARD);
    } else {
      navigate(SCENARIO_PATH);
    }
  };

  const handleDirectoryOnClick = (label) => () => {
    const newDirectories = [...directories];
    newDirectories.forEach((dir) => (dir.isActive = false));
    newDirectories.filter((dir) => dir.label === label)[0].isActive = true;
    setDirectories(newDirectories);

    navigate(newDirectories.filter((dir) => dir.label === label)[0].path);
  };

  const AppContextValue = {
    setIsLoginLoading: setIsLoginLoading,
    setIsFunctionLoading: setIsFunctionLoading,

    AuthService: AuthService,

    inspectedFunctionId: inspectedFunctionId,
    handleChangeInspectedFunctionId: handleChangeInspectedFunctionId,

    collection: collection,
    setCollection: setCollection,
    handleChangeCollection: handleChangeCollection,

    upAndRunningCollection: upAndRunningCollection,
    setUpAndRunningCollection: setUpAndRunningCollection,

    client: client,
    setClient: setClient,

    userInfo: userInfo,
    setUserInfo: setUserInfo,

    inspectedSystem: inspectedSystem,
    handleChangeInspectedSystem: setInspectedSystem,

    inspectedVersion: inspectedVersion,
    handleChangeInspectedVersion: setInspectedVersion,

    specialFunctions: specialFunctions,
    setSpecialFunctions: setSpecialFunctions,

    handleShowSnackbar: handleShowSnackbar,
    handleShowErrorSnackbar: handleShowErrorSnackbar,

    users: users,
    setUsers: setUsers,

    signingKeys: signingKeys,
    setSigningKeys: setSigningKeys,

    devices: devices,
    setDevices: setDevices,

    deviceGroups: deviceGroups,
    setDeviceGroups: setDeviceGroups,

    roles: roles,
    setRoles: setRoles,

    properties: properties,
    setProperties: setProperties,

    roles2properties: roles2properties,
    setRoles2properties: setRoles2properties,

    devicesWithoutClient: devicesWithoutClient,
    setDevicesWithoutClient: setDevicesWithoutClient,

    editorStateList: editorStateList,
    setEditorStateList: setEditorStateList,
    handleAddEditorStateList: handleAddEditorStateList,
  };

  const logout = (e) => {
    e.preventDefault();
    logoutAction();
  };

  const logoutAction = () => {
    redirectToLogin(navigate);
    AuthService.logOut();

    setUserInfo(null);
    setInspectedFunctionId(``);
    setCollection([]);
  };

  const onChangeLanguageClick = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  };

  return (
    <RootContainer>
      <AppContextProvider value={AppContextValue}>
        {userInfo && (
          <SAPTopNav
            onLogoClick={topNavbarLogoClick}
            onProcessesClick={handleDirectoryOnClick(discover)}
            onManagementClick={handleDirectoryOnClick(manage)}
            onLogoutClick={logout}
            onChangeLanguageClick={onChangeLanguageClick}
            userInfo={userInfo}
            variant={isScenarioPage ? "light" : "dark"}
          />
        )}

        <Routes>
          <Route element={<PublicRoute restricted={true} />}>
            <Route element={<LoginPage />} path={LOGIN_PATH} />
            <Route element={<Register />} path={REGISTER_PATH} />
          </Route>
          <Route element={<PrivateRoute />}>
            <Route element={<Scenario />} path={SCENARIO_PATH} />
            <Route
              element={<FunctionDetailsContainer />}
              path={FUNCTION_DETAILS_PATH}
            />
            <Route
              element={<FunctionDetailsContainer />}
              path={SPECIAL_FUNCTIONS_PATH}
            />

            {userInfo && userInfo.role !== "PROSPECT" && (
              <Route
                element={<ManageMainDashboard />}
                path={MANAGE_MAIN_DASHBOARD_PATH}
              />
            )}

            {userInfo && userInfo.role === "ADMIN" && (
              <>
                <Route element={<AdminDashboard />} path={ADMIN_DASHBOARD} />
                <Route
                  element={<BlacklistedDevice />}
                  path={BLACKLISTED_DEVICE}
                />
              </>
            )}
            <Route element={<Scenario />} path={"*"} />
          </Route>
        </Routes>

        {/** Backdrop for login loading */}
        <LoginBackdrop open={isLoginLoading} />
        {/** Backdrop for function loading */}
        <GeneralBackdrop open={isFunctionLoading} />

        <CollectionCartDialog
          open={collectionCartDialogOpen}
          onClose={handleCloseCollectionCartDialog}
        />

        <PricingAndLicenseDialog
          open={pricingAndLicenseDialogOpen}
          onClose={handleClosePricingAndLicenseDialog}
          country={userInfo?.country}
        />

        <UpAndRunningDialog
          open={upAndRunningDialogOpen}
          onClose={handleCloseUpAndRunningDialog}
          country={userInfo?.country}
        />

        {/** Snackbar */}
        <Snackbar
          key={snackbarData.key}
          show={showSnackbar}
          onClick={() => {
            handleCloseSnackbar();
          }}
          icon={<Info style={{ color: ColorSAP.primary[40] }} />}
          showButton={snackbarData.showButton}
          buttonLabel={snackbarData.buttonLabel}
          animationDuration={snackbarData.autoHideDuration}
          backgroundColor={ColorSAP.primary[60]}
          meterColor={ColorSAP.primary[80]}
        >
          {snackbarData.message}
        </Snackbar>
        <Snackbar
          key={errorSnackbarData.key}
          show={showErrorSnackbar}
          onClick={() => {
            handleCloseErrorSnackbar();
          }}
          icon={<Info style={{ color: ColorSAP.danger[10] }} />}
          showButton={true}
          animationDuration={3000}
          backgroundColor={ColorSAP.danger[60]}
          meterColor={ColorSAP.danger[20]}
        >
          {snackbarErrorMessage
            ? snackbarErrorMessage
            : "Something went wrong!"}
        </Snackbar>

        <ScreenSizeWarningBackdrop open={isScreenSizeLimit && !isAuthPage} />
      </AppContextProvider>
    </RootContainer>
  );
};

export default App;
